import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Elements, IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { getStripePromise } from "../../lib/stripe";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { BackBar } from "../../components/BackBar";
import { CheckCircle2, ShieldCheck, AlertCircle, Loader2, Banknote } from "lucide-react";
import { toast } from "sonner";

const IBAN_ELEMENT_OPTIONS = {
  supportedCountries: ["SEPA"],
  placeholderCountry: "FR",
  style: {
    base: {
      fontSize: "16px",
      color: "#14274B",
      "::placeholder": { color: "#94A3B8" },
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI"',
    },
    invalid: { color: "#B91C1C", iconColor: "#B91C1C" },
  },
};

// =============================================================================
// SepaMandateForm — the actual IBAN form. Must be a child of <Elements> so it
// can use Stripe hooks (useStripe / useElements). Confirms the SetupIntent on
// submit, which triggers the `setup_intent.succeeded` webhook on Stripe's side.
// =============================================================================
function SepaMandateForm({ enrollmentId, clientSecret, accountHolderName, accountHolderEmail, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const { lang } = useLang();

  const [name, setName] = useState(accountHolderName || "");
  const [email, setEmail] = useState(accountHolderEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setError(null);
    setSubmitting(true);
    try {
      const iban = elements.getElement(IbanElement);
      const { error: stripeError, setupIntent } = await stripe.confirmSepaDebitSetup(
        clientSecret,
        {
          payment_method: {
            sepa_debit: iban,
            billing_details: { name: name.trim(), email: email.trim() },
          },
        },
      );
      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
        return;
      }
      // Stripe returns setupIntent.status === "succeeded" for test IBANs
      // immediately. In production, SEPA mandates may briefly be "processing".
      // Call the backend finalize endpoint so we don't depend on the webhook
      // (which may take seconds to arrive, or never arrive in preview).
      try {
        await api.post("/parent/sepa/finalize", { enrollment_id: enrollmentId });
      } catch (_) { /* webhook will catch up — non blocking */ }

      toast.success(lang === "fr"
        ? "Mandat SEPA enregistré ✓"
        : "SEPA mandate saved ✓");
      onSuccess?.(setupIntent);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="sepa-mandate-form">
      <div className="grid gap-3">
        <label className="text-sm font-medium">
          {lang === "fr" ? "Nom du titulaire du compte" : "Account holder name"}
        </label>
        <Input
          required value={name} onChange={(e) => setName(e.target.value)}
          placeholder={lang === "fr" ? "Jean Dupont" : "Jane Doe"}
          data-testid="sepa-name-input"
        />
      </div>

      <div className="grid gap-3">
        <label className="text-sm font-medium">Email</label>
        <Input
          required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="parent@example.com"
          data-testid="sepa-email-input"
        />
      </div>

      <div className="grid gap-3">
        <label className="text-sm font-medium">IBAN</label>
        <div className="rounded-md border bg-card px-3 py-3" data-testid="sepa-iban-element">
          <IbanElement options={IBAN_ELEMENT_OPTIONS} />
        </div>
        <p className="text-xs text-muted-foreground">
          {lang === "fr"
            ? "Pour tester en preview, utilisez l'IBAN officiel Stripe : DE89 3704 0044 0532 0130 00"
            : "Test IBAN (preview only): DE89 3704 0044 0532 0130 00"}
        </p>
      </div>

      {/* Data privacy reassurance — Stripe handles bank data, Emergent never touches it */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 text-xs leading-relaxed mb-1">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
          <div className="text-emerald-900/90">
            <strong className="block mb-1 text-emerald-900">
              {lang === "fr" ? "Vos données bancaires restent chez Stripe" : "Your bank details stay with Stripe"}
            </strong>
            {lang === "fr"
              ? "Votre IBAN est transmis directement et exclusivement à Stripe (notre prestataire de paiement certifié PCI-DSS Level 1). OFA France ne le voit, ne le reçoit et ne le stocke à aucun moment — ni dans notre base de données, ni dans nos logs, ni dans nos sauvegardes."
              : "Your IBAN is transmitted directly and exclusively to Stripe (our PCI-DSS Level 1 certified payment provider). OFA France never sees, receives or stores it — neither in our database, our logs, nor our backups."}
          </div>
        </div>
      </div>

      {/* Legal mandate text — required by SEPA regulations */}
      <div className="rounded-lg border border-muted bg-muted/30 p-4 text-xs text-muted-foreground leading-relaxed">
        {lang === "fr"
          ? "En fournissant vos informations de paiement et en confirmant ce paiement, vous autorisez (A) OFA France et Stripe, notre prestataire de services de paiement, à envoyer des instructions à votre banque pour débiter votre compte et (B) votre banque à débiter votre compte conformément à ces instructions. Vous bénéficiez d'un droit à remboursement de la part de votre banque selon les conditions générales du contrat conclu avec elle. Toute demande de remboursement doit être faite dans les huit semaines suivant la date à laquelle votre compte a été débité."
          : "By providing your payment information and confirming this payment, you authorise (A) OFA France and Stripe, our payment service provider, to send instructions to your bank to debit your account and (B) your bank to debit your account in accordance with those instructions. You are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited."}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 text-destructive text-sm p-3" data-testid="sepa-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={!stripe || submitting}
        className="w-full"
        data-testid="sepa-confirm-btn"
      >
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        <ShieldCheck className="w-4 h-4 mr-2" />
        {lang === "fr" ? "Autoriser les prélèvements" : "Authorize debits"}
      </Button>
    </form>
  );
}


// =============================================================================
// SepaMandatePage — the actual route. Loads enrollment + creates SetupIntent,
// then renders <Elements> with the client_secret so the form can confirm it.
// =============================================================================
export default function SepaMandatePage() {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();

  const [clientSecret, setClientSecret] = useState(null);
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [mandateStatus, setMandateStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const stripePromise = useMemo(() => getStripePromise(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Step 1: check current mandate status
        const status = await api.get(`/parent/sepa/status`, { params: { enrollment_id: enrollmentId } });
        if (cancelled) return;
        setMandateStatus(status.data);

        if (status.data.mandate_active) {
          setAlreadyActive(true);
          setLoading(false);
          return;
        }
        if (!status.data.required) {
          // 1× plan — no SEPA needed. Bounce back.
          toast.info(lang === "fr"
            ? "Aucun mandat SEPA requis pour votre plan."
            : "No SEPA mandate required for your plan.");
          navigate("/parent/payment");
          return;
        }

        // Step 2: ask backend for a SetupIntent client_secret
        const r = await api.post("/parent/sepa/setup-intent", { enrollment_id: enrollmentId });
        if (cancelled) return;
        if (r.data.already_active) {
          setAlreadyActive(true);
        } else {
          setClientSecret(r.data.client_secret);
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId]);

  const handleSuccess = () => {
    setAlreadyActive(true);
    // Give the webhook a moment to persist on the backend, then refresh status
    setTimeout(async () => {
      try {
        const status = await api.get(`/parent/sepa/status`, { params: { enrollment_id: enrollmentId } });
        setMandateStatus(status.data);
      } catch { /* swallow */ }
    }, 2500);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-12 py-8" data-testid="parent-sepa-mandate-page">
      <BackBar to="/parent/payment" fallbackLabelFr="Mon plan" fallbackLabelEn="My plan" />
      <p className="overline mb-2">{lang === "fr" ? "Prélèvement automatique" : "Direct debit"}</p>
      <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">
        {lang === "fr" ? "Autorisation de prélèvement SEPA" : "SEPA mandate authorization"}
      </h1>
      <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
        {lang === "fr"
          ? "Pour automatiser vos échéances mensuelles ou semestrielles, OFA France a besoin d'un mandat de prélèvement SEPA. Cette autorisation est unique : vos prélèvements se feront ensuite automatiquement le 15 de chaque mois."
          : "To automate your monthly or biannual installments, OFA France needs a SEPA Direct Debit mandate. You authorize once; subsequent debits will run automatically on the 15th of each month."}
      </p>

      {loading && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="sepa-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
          {lang === "fr" ? "Préparation du formulaire sécurisé…" : "Preparing the secure form…"}
        </CardContent></Card>
      )}

      {!loading && error && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="p-6" data-testid="sepa-page-error">
            <div className="flex items-start gap-2 text-destructive mb-2">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <strong>{lang === "fr" ? "Erreur" : "Error"}</strong>
            </div>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {!loading && alreadyActive && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="sepa-already-active">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900 mb-1">
                  {lang === "fr" ? "Mandat SEPA actif" : "SEPA mandate active"}
                </h3>
                <p className="text-sm text-emerald-900/80">
                  {lang === "fr"
                    ? "Vos prélèvements automatiques sont configurés. Les échéances seront débitées sur le 15 de chaque mois."
                    : "Your direct debits are set up. Installments will be drawn on the 15th of each month."}
                </p>
                {mandateStatus?.last4 && (
                  <Badge variant="outline" className="mt-3 gap-1.5">
                    <Banknote className="w-3 h-3" />
                    IBAN •••• {mandateStatus.last4}
                    {mandateStatus.bank_code && <span className="text-muted-foreground"> · {mandateStatus.bank_code}</span>}
                  </Badge>
                )}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <Button onClick={() => navigate("/parent/payment")} data-testid="sepa-go-plan">
                {lang === "fr" ? "Voir mon plan" : "View my plan"}
              </Button>
              <Button variant="outline" onClick={() => navigate("/parent")} data-testid="sepa-go-dashboard">
                {lang === "fr" ? "Retour au tableau de bord" : "Back to dashboard"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && clientSecret && !alreadyActive && stripePromise && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {lang === "fr" ? "Vos coordonnées bancaires" : "Your bank details"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Elements stripe={stripePromise} options={{ clientSecret, locale: lang === "fr" ? "fr" : "en" }}>
              <SepaMandateForm enrollmentId={enrollmentId} clientSecret={clientSecret} onSuccess={handleSuccess} />
            </Elements>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
